import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { OwlDateTimeModule, OwlNativeDateTimeModule } from '@danielmoncada/angular-datetime-picker';
import { Ng6AgencyTextComponent } from './inputs/text/ng6-agency-text.component';
import { Ng6AgencyRichTextComponent } from './inputs/rich-text/ng6-agency-rich-text.component';
import { Ng6AgencyNumberComponent } from './inputs/number/ng6-agency-number.component';
import { Ng6AclSelectComponent } from './inputs/acl-selector/ng6-acl-selector.component';
import { Ng6AgencyCheckboxComponent } from './inputs/checkbox/ng6-agency-checkbox.component';
import { Ng6AgencyTextareaComponent } from './inputs/textarea/ng6-agency-textarea.component';
import { Ng6AgencyDatePickerComponent } from './inputs/datepicker/ng6-agency-datepicker.component';
import { RemoveButtonComponent } from './content/buttons/remove/remove-button.component';
import { ResponseTypePipe } from './utils/get-enum/get-response-type';
import { OrangeButtonComponent } from './content/buttons/orange/orange-button.component';
import { ConfirmationModalComponent } from './modals/confirmation-modal/confirmation-modal.component';
import { GreenCheckmarkComponent } from './content/badges/green-checkmark/green-checkmark.component';
import { ActivitiesSupportPipe } from './utils/get-enum/get-activities-support';
import { PerformanceCategoriesSupportPipe } from './utils/get-enum/get-performance-categories-support';
import { QuestionSupportPipe } from './utils/get-enum/get-question-support';
import { FormActionPipe } from './utils/get-enum/get-form-action';
import { InfoModalComponent } from './modals/info-modal/info-modal.component';
import { SpinnerComponent } from './content/spinner/spinner.component';
import { NothingMessageComponent } from './content/nothing-message/nothing-message.component';
import { ErrorStateComponent } from './content/error-state/error-state.component';
import { AgencyDatePipe } from './pipes/agency-date.pipe';
import { RelativeTimePipe } from './pipes/relative-time.pipe';
import { Ng6AgencyRatingsSelectComponent } from './inputs/ratings-select/ng6-agency-ratings-select.component';
import { EditButtonComponent } from './content/buttons/edit/edit-button.component';
import { RatingClassPipe } from './pipes/rating-class.pipe';
import { RatingDisplayPipe } from './pipes/rating-display.pipe';
import { RatingScoreDisplayPipe } from './pipes/rating-score-display.pipe';
import { MinToStringPipe } from './pipes/min-to-string.pipe';
import { CompletionThumbsMarkClassPipe } from './pipes/completion-thumbs-mark-class.pipe';
import { ByIdPipe } from './pipes/by-id.pipe';
import { ActiveInactiveBtnsComponent } from './content/active-inactive-btns/active-inactive-btns.component';
import { ToggledArrComponent } from './content/toggled-arr/toggled-arr.component';
import { FroalaEditorModule, FroalaViewModule } from 'angular-froala-wysiwyg';
import { DescriptionLinkComponent } from './content/description-link/description-link.component';
import { DescriptionModalComponent } from './modals/description-modal/description-modal.component';
import { Ng6AgencyGroupSelectComponent } from './inputs/group-select/ng6-agency-group-select.component';
import { ResponsePipe } from './utils/get-enum/get-response';
import { QuizResponsePipe } from './utils/get-enum/get-quiz-response';
import { InputErrorComponent } from './content/input-error/input-error.component';
import { RejectSummariesModalComponent } from './modals/reject-summaries-modal/reject-summaries-modal.component';
import { RacePipe } from './utils/get-enum/get-race';
import { GrayButtonComponent } from './content/buttons/gray/gray-button.component';
import { Ng6MultiSelectComponent } from './inputs/multi-selector/ng6-multi-select.component';
import { Ng6RangeDatePickerComponent } from './inputs/range-datepicker/ng6-range-datepicker.component';
import { BlueButtonComponent } from './content/buttons/blue/blue-button.component';
import { BackButtonComponent } from './content/buttons/back/back-button.component';
import { EditDescriptionModalComponent } from './modals/edit-description-modal/edit-description-modal.component';
import { ModalLinkComponent } from './content/modal-link/modal-link.component';
import { TabSheetComponent } from './content/tab-sheet/tab-sheet.component';
import { QuizQuestionTypePipe } from './utils/get-enum/get-quiz-question-type';
import { Ng6SwitchComponent } from './inputs/switch/ng6-switch.component';
import { SwitchBallComponent } from './inputs/switch-ball/switch-ball.component';
import { Ng6AclGroupSelectorComponent } from './inputs/acl-group-selector/ng6-acl-group-selector.component';
import { QuizResponseTypePipe } from './utils/get-enum/get-quiz-response-type';
import { ProgramRolePipe } from './utils/get-enum/get-program-role';
import { QuestionTypePipe } from './utils/get-enum/get-question-type';
import { ByFullNamePipe } from './pipes/by-full-name.pipe';
import { AssignmentIconComponent } from './content/assignment-icon/assignment-icon.component';
import { ProgramTypePipe } from './utils/get-enum/get-program-type';
import { ByTitlePipe } from './pipes/by-title.pipe';
import { ArraySortByPropPipe } from './pipes/sort-by-prop.pipe';
import { SiteSortFieldPipe } from './utils/get-enum/get-site-sort';
import { ByNamePipe } from './pipes/by-name.pipe';
import { PeFormResponseTypePipe } from './utils/get-enum/get-pe-form-response-type';
import { MandatoryBadgeComponent } from './content/badges/mandatory-badge/mandatory-badge.component';
import { PeFormQuestionTypePipe } from './utils/get-enum/get-pe-form-question-type';
import { FilterByPropPipe } from './pipes/filter-by-prop.pipe';
import { FilterByPropsPipe } from './pipes/filter-by-item-props.pipe';
import { TrendComponent } from './content/trend/trend.component';
import { SkillProgressComponent } from './content/skill-progress/skill-progress.component';
import { PeFormResponsePipe } from './utils/get-enum/get-pe-form-response';
import { ScoreInputComponent } from './inputs/score-input/score-input.component';
import { ByActivePipe } from './pipes/by-active.pipe';
import { NgxPaginationModule } from 'ngx-pagination';
import { DonutWithTableComponent } from './content/donut-with-table/donut-with-table.component';
import { AssignmentStatusPipe } from './utils/get-enum/get-assignment-status';
import { GenderPipe } from './utils/get-sex';
import { ByRacePipe } from './pipes/by-race.pipe';
import { ByGenderPipe } from './pipes/by-sex.pipe';
import { BySelectedPipe } from './pipes/by-selected.pipe';
import { DragulaModule } from 'ng2-dragula';
import { ImageCropperComponent } from 'ngx-image-cropper';
import { FileSize } from './pipes/file-size.pipe';
import { DropdownButtonComponent } from 'src/app/shared/content/buttons/dropdown/dropdown-button.component';
import { ExportButtonComponent } from 'src/app/shared/content/buttons/export/export-button.component';
import { XlsxButtonComponent } from 'src/app/shared/content/buttons/xlsx/xlsx-button.component';
import { PdfButtonComponent } from 'src/app/shared/content/buttons/pdf/pdf-button.component';
import { ProgressBarComponent } from './content/progress-bar/progress-bar.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ImageEditComponent } from './inputs/image-edit/image-edit.component';
import { TraineeNameComponent } from 'src/app/shared/content/trainee-name/trainee-name.component';
import { TrendingGroupByOptionPipe } from 'src/app/shared/utils/get-enum/get-trending-group-by-option';
import { RelatedCategoryFormInstanceComponent } from 'src/app/shared/content/performance-summary-item/related-category-form-instance/related-category-form-instance.component';
import { PerformanceSummaryItemComponent } from 'src/app/shared/content/performance-summary-item/performance-summary-item.component';
import { PerformanceSummaryHeadingComponent } from 'src/app/shared/content/performance-summary-heading/performance-summary-heading.component';
import { RectangleRatingComponent } from 'src/app/shared/content/rectangle-rating/rectangle-rating.component';
import { FileSaverModule } from 'ngx-filesaver';
import { ToastNoAnimationModule, ToastrModule } from 'ngx-toastr';
import { TrainerToOtherRolesPipe } from 'src/app/shared/pipes/trainer-to-other-roles.pipe';
import { SelectTargetModalComponent } from 'src/app/shared/modals/select-target-modal/select-target-modal.component';
import { AlertListModalComponent } from 'src/app/shared/modals/alert-list-modal/alert-list-modal.component';
import { TraineeToOtherRolesPipe } from 'src/app/shared/pipes/trainee-to-other-roles.pipe';
import { UserSelectComponent } from './inputs/user-select/user-select.component';
import { ByPropPipe } from './pipes/by-prop.pipe';
import { ReasonModalComponent } from 'src/app/shared/modals/reason-modal/reason-modal.component';
import { SortingHeadingComponent } from './content/sorting-heading/sorting-heading.component';
import { TextImageEditorComponent } from './inputs/text-image-editor/text-image-editor.component';
import { ResourceImageModalComponent } from './modals/resource-image-modal/resource-image-modal.component';
import { AlertButtonComponent } from './content/buttons/alert/alert-button.component';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { ProgramFilterPipe } from 'src/app/shared/utils/get-enum/get-program-filter';
import { IconSelectComponent } from './inputs/icon-select/icon-select.component';
import { FormActionBtnPipe } from 'src/app/shared/utils/get-enum/get-form-action-btn';
import { DocPipe } from 'src/app/shared/utils/get-enum/get-doc';
import { ImageFormatPipe } from 'src/app/shared/utils/get-enum/get-image-format';
import { DocResourcesPipe } from 'src/app/shared/utils/get-enum/get-doc-resources';
import { CommonResponsesModalComponent } from 'src/app/shared/modals/common-responses-modal/common-responses-modal.component';
import { BreadcrumbsComponent } from 'src/app/shared/content/breadcrumbs/breadcrumbs.component';
import { ActiveDirectoryTypePipe } from 'src/app/shared/utils/get-enum/get-active-directory-type';
import { HiddenDottedMenuComponent } from './content/hidden-dotted-menu/hidden-dotted-menu.component';
import { CrossComponent } from './content/cross/cross.component';
import { AttachmentListComponent } from './inputs/attachment-list/attachment-list.component';
import { AddButtonComponent } from './content/buttons/add/add-button.component';
import { SpinnerButtonComponent } from 'src/app/shared/content/buttons/spinner/spinner-button.component';
import { DraggableAreaComponent } from 'src/app/shared/content/draggable-area/draggable-area.component';
import { ControlDisabledPipe } from 'src/app/shared/pipes/control-disabled.pipe';
import { FormBadgeClassPipe } from 'src/app/shared/pipes/form-badge-class.pipe';
import { Ng6AgencyMultiSelectComponent } from 'src/app/shared/inputs/multi-select/ng6-agency-multi-select.component';
import { ChangeInitialPasswordModalComponent } from 'src/app/shared/modals/change-initial-password-modal/change-initial-password-modal.component';
import { ImageSelectComponent } from './inputs/a360-image-select/a360-image-select.component';
import { Ng6AgencyCustomSelectComponent } from './inputs/ng6-agency-custom-select/ng6-agency-custom-select.component';
import { FilterByPipe } from './pipes/filter-by.pipe';
import { ActiveIndicatorComponent } from 'src/app/shared/content/active-indicator/active-indicator.component';
import { PageHeaderComponent } from 'src/app/shared/content/page-header/page-header.component';
import { Ng6AgencyTabComponent } from './content/ng6-agency-tabset/ng6-agency-tab/ng6-agency-tab.component';
import { Ng6AgencyTabsetComponent } from './content/ng6-agency-tabset/ng6-agency-tabset.component';
import { NumeralPipe } from 'src/app/shared/pipes/numeral.pipe';
import { IncidentsSupportPipe } from './utils/get-enum/get-incidents-support';
import { Ng6AgencyRadioOptionComponent } from './inputs/ng6-agency-radio-option/ng6-agency-radio-option.component';
import { Ng6AgencyRadioGroupComponent } from './inputs/ng6-agency-radio-group/ng6-agency-radio-group.component';
import { InstanceActionsBarComponent } from './content/instance-actions-bar/instance-actions-bar.component';
import { InstanceNumberPipe } from './pipes/instance-number.pipe';
import { OutlineButtonComponent } from 'src/app/shared/content/buttons/outline/outline-button.component';
import { IncidentNatureComponent } from './content/incident-nature/incident-nature.component';
import { IncidentNaturePipe } from './utils/get-enum/get-incident-nature';
import { FormInstanceLinkComponent } from './content/form-instance-link/form-instance-link.component';
import { SimpleBarChartComponent } from './content/simple-bar-chart/simple-bar-chart.component';
import { RejectPeSummariesModalComponent } from 'src/app/shared/modals/reject-pe-summaries-modal/reject-pe-summaries-modal.component';
import { FtReportSubjectPipe } from './utils/get-enum/get-ft-report-subject';
import { ReportStatusPipe } from './utils/get-enum/get-report-status';
import { NumberWithoutCommaPipe } from './pipes/number-without-comma.pipe';
import { SplitButtonComponent } from 'src/app/shared/content/buttons/split-button/split-button.component';
import { ItemCountWithHoverComponent } from 'src/app/shared/content/item-count-with-hover/item-count-with-hover.component';
import { SelectUserModalRowComponent } from './modals/select-user-modal/select-user-modal-row/select-user-modal-row.component';
import { SelectUserModalComponent } from './modals/select-user-modal/select-user-modal.component';
import { ChartComponent } from './content/chart/chart.component';
import { A360SectionWithHeaderComponent } from './content/section-with-header/section-with-header.component';
import { GoalStatusPipe } from './utils/get-enum/get-goal-status';
import { ByGoalStatusPipe } from './pipes/by-goal-status.pipe';
import { A360CardComponent } from './content/a360-card/a360-card.component';
import { DocumentsListComponent } from './inputs/documents-list/documents-list.component';
import { DocumentItemComponent } from './inputs/documents-list/document-item/document-item.component';
import { DocumentIconComponent } from './content/document-icon/document-icon.component';
import { DueDateFilterPipe } from './pipes/due-date-filter.pipe';
import { DueDatePipe } from './utils/get-enum/get-due-date';
import { EmptyStateComponent } from './content/empty-state/empty-state.component';
import { ButtonWithDropdownComponent } from 'src/app/shared/content/buttons/button-with-dropdown/button-with-dropdown.component';
import { ProfileGoalsComponent } from './content/profile/profile-goals/profile-goals.component';
import { UserGoalComponent } from './content/profile/profile-goals/goal-item/goal.component';
import { GoalModificationModalComponent } from './content/profile/profile-goals/goal-modal/goal-modal.component';
import { InterestsReadComponent } from './content/profile/interests-read/interests-read.component';
import { CurrentStepComponent } from './content/current-step/current-step.component';
import { HeaderUserInfoComponent } from './content/header-user-info/header-user-info.component';
import { InstancesListComponent } from './content/instances-list/instances-list.component';
import { InstanceItemComponent } from './content/instances-list/instance-item/instance-item.component';
import { SubjectNarrativeComponent } from './content/subject-narrative/subject-narrative.component';
import { ReadMoreComponent } from './content/read-more/read-more.component';
import { HighFiveViewModalComponent } from './modals/high-five-view-modal/high-five-view-modal.component';
import { AlertTypePipe } from './utils/get-enum/get-alert-type';
import { SettingItemTemplateComponent } from './content/settings-list/setting-item-template/setting-item-template.component';
import { BorderedButtonComponent } from './content/buttons/bordered-button/bordered-button.component';
import { MtAssignmentModalComponent } from './modals/mt-assignment-modal/mt-assignment-modal.component';
import { AlertBadgeComponent } from './content/alert-badge/alert-badge.component';
import { AuditLogComponentPipe } from './utils/get-enum/get-audit-log-component';
import { LazySlicePipe } from './pipes/lazy-slice.pipe';
import { ViewDetailsComponent } from './content/view-details/view-details.component';
import { AuditLogEventPipe } from './utils/get-enum/get-audit-log-event';
import { ActiveBadgeComponent } from './content/badges/active-badge/active-badge.component';
import { PerformanceCategoriesAverageComponent } from './content/performance-categories-average/performance-categories-average.component';
import { PrintButtonComponent } from './content/buttons/print/print-button.component';
import { PrintBackdropComponent } from './content/print-backdrop/print-backdrop.component';
import { ProgramExitTypeNaturePipe } from './utils/get-enum/get-program-exit-type-nature';
import { ProgramExitTypeNatureComponent } from './content/program-exit-type-nature/program-exit-type-nature.component';
import { A360ImageMultiSelectComponent } from './inputs/a360-image-multi-select/a360-image-multi-select.component';
import { A360AvatarComponent } from './content/a360-avatar/a360-avatar.component';
import { PowerDmsFilePickerComponent } from './content/power-dms-file-picker/power-dms-file-picker.component';
import { PolicyDocumentReviewListComponent } from './content/policy-document-review-list/policy-document-review-list.component';
import {
  MoveToMainDirective,
  NoFocusStealingDirective,
  OutsideClickDirective,
  ResizedDirective,
  ScrollOnClickDirective,
  IconDirective,
  TableScrollCheckDirective,
  DynamicIdDirective,
  AutIdDirective,
  ScrollToFirstInvalidDirective
} from './directives';
import { SupportingDocumentsComponent } from './content/supporting-documents/supporting-documents.component';
import { SnapshotCardComponent } from './content/snapshot-card/snapshot-card.component';
import { NumberWithComparatorComponent } from './content/number-with-comparator/number-with-comparator.component';
import { StorageUsageComponent } from './content/storage-usage/storage-usage.component';
import { UsersRowExpandableComponent } from './content/users-row-expandable/users-row-expandable.component';
import { TooltipComponent } from './content/tooltip/tooltip.component';
import {
  TrainingCourseClassStatusFilterPipe,
  TrainingCourseClassStatusPipe,
  TrainingCourseGradingPipe,
  TrainingCourseTypePipe,
  TrainingCourseRequestStatusPipe,
  QuizStatusPipe,
  TrainingCourseProviderPipe
} from './utils/get-enum';
import { A360AvatarsComponent } from './content/a360-avatars/a360-avatars.component';
import { DocumentsReadOnlyListComponent } from './content/documents-read-only-list/documents-read-only-list.component';
import { StartQuizModalComponent } from './modals';
import { ProfileTrainingCourseClassesComponent } from './content/profile/profile-training-course-classes/profile-training-course-classes.component';
import { ProfileTrainingCourseClassComponent } from './content/profile/profile-training-course-classes/profile-training-course-class/profile-training-course-class.component';
import { GreyoutWrapComponent } from './content/greyout-wrap/greyout-wrap.component';
import { VersionComponent } from './content/version/version.component';
import { LabelComponent } from './content/label/label.component';
import { ButtonComponent } from './content/button/button.component';
import { IconComponent } from './content/icon/icon.component';
import { InputComponent } from './content/input/input.component';
import { TableRowComponent } from './content/table/table-row/table-row.component';
import { TableDataComponent } from './content/table/table-data/table-data.component';
import { MaintenanceModeComponent } from './content/maintenance-mode/maintenance-mode.component';
import { StripHtmlPipe } from '@app/shared/pipes/strip-html.pipe';
import { ToastComponent } from './content/toast/toast.component';
import { LogoComponent } from '@app/shared/content/logo/logo.component';
import { TimePickerComponent } from '@app/shared/inputs/time-picker/time-picker.component';
import { SafePipe } from '@app/shared/pipes/safe.pipe';
import { MigrateQuillPipe } from '@app/shared/pipes/migrate-quill.pipe';
import { PatternLibraryModule } from './pattern-library.module';
import { IconBadgeComponent } from './content/icon-badge/icon-badge.component';
import { UserInfoCardComponent } from './content/user-info-card/user-info-card.component';
import { AlertListFlyoutComponent } from './flyouts/alert-list-flyout/alert-list-flyout.component';
import { IconsModule } from './icons.module';
import { ProductCodeToNamePipe } from 'src/app/shared/pipes/product-code-to-name.pipe';
import { ToggleArrowBtnComponent } from './content/toggle-arrow-btn/toggle-arrow-btn.component';
import { PageNotAvailableComponent } from './content/page-not-available.component';
import { RecallScoresCardComponent } from '@app/shared/content/recall-scores-card/recall-scores-card.component';

@NgModule({
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    A11yModule,
    FroalaEditorModule.forRoot(),
    FroalaViewModule.forRoot(),
    NgxPaginationModule,
    RouterModule,
    IconsModule,
    DragulaModule,
    FileSaverModule,
    ToastNoAnimationModule.forRoot({
      timeOut: 5000,
      positionClass: 'toast-top-center',
      closeButton: true,
      tapToDismiss: false,
      toastComponent: ToastComponent
    }),
    TooltipModule,
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    ScrollingModule,
    PatternLibraryModule,
    ImageCropperComponent,
    RecallScoresCardComponent
  ],
  declarations: [
    Ng6AgencyTextComponent,
    Ng6AgencyRichTextComponent,
    Ng6MultiSelectComponent,
    Ng6AclSelectComponent,
    Ng6AgencyTextareaComponent,
    Ng6AgencyNumberComponent,
    Ng6AgencyCheckboxComponent,
    Ng6AgencyDatePickerComponent,
    Ng6RangeDatePickerComponent,
    ResponseTypePipe,
    ActivitiesSupportPipe,
    PerformanceCategoriesSupportPipe,
    QuestionSupportPipe,
    AgencyDatePipe,
    RelativeTimePipe,
    RatingClassPipe,
    RatingDisplayPipe,
    RatingScoreDisplayPipe,
    MinToStringPipe,
    CompletionThumbsMarkClassPipe,
    FormActionPipe,
    ResponsePipe,
    QuizResponsePipe,
    OrangeButtonComponent,
    RemoveButtonComponent,
    EditButtonComponent,
    ConfirmationModalComponent,
    SpinnerComponent,
    NothingMessageComponent,
    ErrorStateComponent,
    InfoModalComponent,
    GreenCheckmarkComponent,
    InfoModalComponent,
    ByIdPipe,
    ByFullNamePipe,
    ByTitlePipe,
    ArraySortByPropPipe,
    ActiveInactiveBtnsComponent,
    ToggledArrComponent,
    Ng6AgencyRatingsSelectComponent,
    IconSelectComponent,
    DescriptionModalComponent,
    DescriptionLinkComponent,
    Ng6AgencyGroupSelectComponent,
    InputErrorComponent,
    BackButtonComponent,
    EditDescriptionModalComponent,
    ModalLinkComponent,
    RacePipe,
    RejectSummariesModalComponent,
    GrayButtonComponent,
    BlueButtonComponent,
    TabSheetComponent,
    QuizQuestionTypePipe,
    Ng6SwitchComponent,
    Ng6AclGroupSelectorComponent,
    ControlDisabledPipe,
    QuizResponseTypePipe,
    ProgramRolePipe,
    QuestionTypePipe,
    AssignmentIconComponent,
    ProgramTypePipe,
    SiteSortFieldPipe,
    ByNamePipe,
    ByNamePipe,
    ProgramTypePipe,
    PeFormResponseTypePipe,
    MandatoryBadgeComponent,
    PeFormQuestionTypePipe,
    FilterByPropPipe,
    FilterByPropsPipe,
    ByPropPipe,
    TrendComponent,
    SkillProgressComponent,
    PeFormResponsePipe,
    ScoreInputComponent,
    ByActivePipe,
    DonutWithTableComponent,
    GenderPipe,
    AssignmentStatusPipe,
    BySelectedPipe,
    AssignmentStatusPipe,
    ByRacePipe,
    ByGenderPipe,
    ResizedDirective,
    FileSize,
    DropdownButtonComponent,
    ExportButtonComponent,
    XlsxButtonComponent,
    PdfButtonComponent,
    ProgressBarComponent,
    ImageEditComponent,
    TraineeNameComponent,
    TrendingGroupByOptionPipe,
    RelatedCategoryFormInstanceComponent,
    PerformanceSummaryItemComponent,
    PerformanceSummaryHeadingComponent,
    RectangleRatingComponent,
    TrainerToOtherRolesPipe,
    TraineeToOtherRolesPipe,
    AlertListModalComponent,
    UserSelectComponent,
    SelectTargetModalComponent,
    ReasonModalComponent,
    SortingHeadingComponent,
    TextImageEditorComponent,
    ResourceImageModalComponent,
    AlertButtonComponent,
    ProgramFilterPipe,
    FormActionBtnPipe,
    DocPipe,
    ImageFormatPipe,
    DocResourcesPipe,
    CommonResponsesModalComponent,
    BreadcrumbsComponent,
    ActiveDirectoryTypePipe,
    HiddenDottedMenuComponent,
    CrossComponent,
    AddButtonComponent,
    AttachmentListComponent,
    SpinnerButtonComponent,
    DraggableAreaComponent,
    NoFocusStealingDirective,
    OutsideClickDirective,
    DynamicIdDirective,
    AutIdDirective,
    ScrollToFirstInvalidDirective,
    FormBadgeClassPipe,
    ImageSelectComponent,
    A360ImageMultiSelectComponent,
    Ng6AgencyCustomSelectComponent,
    FilterByPipe,
    Ng6AgencyMultiSelectComponent,
    ChangeInitialPasswordModalComponent,
    ActiveIndicatorComponent,
    PageHeaderComponent,
    Ng6AgencyTabsetComponent,
    Ng6AgencyTabComponent,
    NumeralPipe,
    IncidentsSupportPipe,
    Ng6AgencyRadioOptionComponent,
    Ng6AgencyRadioGroupComponent,
    InstanceActionsBarComponent,
    InstanceNumberPipe,
    OutlineButtonComponent,
    IncidentNatureComponent,
    IncidentNaturePipe,
    FormInstanceLinkComponent,
    SimpleBarChartComponent,
    MoveToMainDirective,
    RejectPeSummariesModalComponent,
    FtReportSubjectPipe,
    ReportStatusPipe,
    NumberWithoutCommaPipe,
    SplitButtonComponent,
    ItemCountWithHoverComponent,
    SelectUserModalRowComponent,
    SelectUserModalComponent,
    ChartComponent,
    A360SectionWithHeaderComponent,
    GoalStatusPipe,
    ByGoalStatusPipe,
    SwitchBallComponent,
    A360CardComponent,
    A360AvatarComponent,
    DocumentsListComponent,
    DocumentItemComponent,
    DocumentIconComponent,
    DueDatePipe,
    DueDateFilterPipe,
    EmptyStateComponent,
    ButtonWithDropdownComponent,
    DocumentIconComponent,
    ProfileGoalsComponent,
    UserGoalComponent,
    GoalModificationModalComponent,
    InterestsReadComponent,
    SubjectNarrativeComponent,
    CurrentStepComponent,
    HeaderUserInfoComponent,
    InstanceItemComponent,
    InstancesListComponent,
    ReadMoreComponent,
    ViewDetailsComponent,
    HighFiveViewModalComponent,
    AlertTypePipe,
    SettingItemTemplateComponent,
    BorderedButtonComponent,
    MtAssignmentModalComponent,
    AlertBadgeComponent,
    AuditLogComponentPipe,
    AuditLogEventPipe,
    LazySlicePipe,
    ActiveBadgeComponent,
    PerformanceCategoriesAverageComponent,
    PrintButtonComponent,
    PrintBackdropComponent,
    ProgramExitTypeNaturePipe,
    ProgramExitTypeNatureComponent,
    ScrollOnClickDirective,
    PowerDmsFilePickerComponent,
    PolicyDocumentReviewListComponent,
    SupportingDocumentsComponent,
    SnapshotCardComponent,
    NumberWithComparatorComponent,
    StorageUsageComponent,
    UsersRowExpandableComponent,
    TooltipComponent,
    TrainingCourseTypePipe,
    TrainingCourseGradingPipe,
    TrainingCourseClassStatusFilterPipe,
    A360AvatarsComponent,
    DocumentsReadOnlyListComponent,
    TrainingCourseClassStatusPipe,
    TrainingCourseRequestStatusPipe,
    QuizStatusPipe,
    StartQuizModalComponent,
    TrainingCourseProviderPipe,
    ProfileTrainingCourseClassesComponent,
    ProfileTrainingCourseClassComponent,
    GreyoutWrapComponent,
    VersionComponent,
    LabelComponent,
    IconComponent,
    IconDirective,
    TableScrollCheckDirective,
    InputComponent,
    ButtonComponent,
    TableRowComponent,
    TableDataComponent,
    MaintenanceModeComponent,
    StripHtmlPipe,
    ToastComponent,
    LogoComponent,
    TimePickerComponent,
    SafePipe,
    MigrateQuillPipe,
    IconBadgeComponent,
    UserInfoCardComponent,
    AlertListFlyoutComponent,
    ProductCodeToNamePipe,
    ToggleArrowBtnComponent,
    PageNotAvailableComponent
  ],
  exports: [
    ToastrModule,
    DragulaModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ScrollingModule,
    Ng6AgencyTextComponent,
    Ng6AgencyRichTextComponent,
    Ng6MultiSelectComponent,
    Ng6AclSelectComponent,
    Ng6AgencyNumberComponent,
    Ng6AgencyCheckboxComponent,
    Ng6AgencyTextareaComponent,
    Ng6AgencyDatePickerComponent,
    Ng6RangeDatePickerComponent,
    ResponseTypePipe,
    ActivitiesSupportPipe,
    PerformanceCategoriesSupportPipe,
    QuestionSupportPipe,
    AgencyDatePipe,
    RelativeTimePipe,
    RatingClassPipe,
    RatingDisplayPipe,
    RatingScoreDisplayPipe,
    MinToStringPipe,
    CompletionThumbsMarkClassPipe,
    FormActionPipe,
    ResponsePipe,
    QuizResponsePipe,
    OrangeButtonComponent,
    RemoveButtonComponent,
    EditButtonComponent,
    ConfirmationModalComponent,
    SpinnerComponent,
    NothingMessageComponent,
    ErrorStateComponent,
    InfoModalComponent,
    GreenCheckmarkComponent,
    InfoModalComponent,
    ByIdPipe,
    ByFullNamePipe,
    ByTitlePipe,
    ArraySortByPropPipe,
    ActiveInactiveBtnsComponent,
    ToggledArrComponent,
    Ng6AgencyRatingsSelectComponent,
    IconSelectComponent,
    DescriptionModalComponent,
    DescriptionLinkComponent,
    Ng6AgencyGroupSelectComponent,
    InputErrorComponent,
    RejectSummariesModalComponent,
    RacePipe,
    GrayButtonComponent,
    BlueButtonComponent,
    BackButtonComponent,
    EditDescriptionModalComponent,
    ModalLinkComponent,
    TabSheetComponent,
    QuizQuestionTypePipe,
    Ng6SwitchComponent,
    Ng6AclGroupSelectorComponent,
    ControlDisabledPipe,
    QuizResponseTypePipe,
    FroalaEditorModule,
    FroalaViewModule,
    ProgramRolePipe,
    QuestionTypePipe,
    AssignmentIconComponent,
    ProgramTypePipe,
    SiteSortFieldPipe,
    ByNamePipe,
    ByNamePipe,
    ProgramTypePipe,
    PeFormResponseTypePipe,
    MandatoryBadgeComponent,
    PeFormQuestionTypePipe,
    FilterByPropPipe,
    FilterByPropsPipe,
    ByPropPipe,
    TrendComponent,
    SkillProgressComponent,
    PeFormResponsePipe,
    ScoreInputComponent,
    ByActivePipe,
    GenderPipe,
    NgxPaginationModule,
    DonutWithTableComponent,
    AssignmentStatusPipe,
    ResizedDirective,
    BySelectedPipe,
    ByGenderPipe,
    ByRacePipe,
    FileSize,
    DropdownButtonComponent,
    ExportButtonComponent,
    XlsxButtonComponent,
    PdfButtonComponent,
    ProgressBarComponent,
    ImageEditComponent,
    FileSaverModule,
    TraineeNameComponent,
    TrendingGroupByOptionPipe,
    PerformanceSummaryItemComponent,
    PerformanceSummaryHeadingComponent,
    RectangleRatingComponent,
    TrainerToOtherRolesPipe,
    TraineeToOtherRolesPipe,
    AlertListModalComponent,
    UserSelectComponent,
    SelectTargetModalComponent,
    SortingHeadingComponent,
    TextImageEditorComponent,
    ResourceImageModalComponent,
    AlertButtonComponent,
    BsDropdownModule,
    ProgramFilterPipe,
    FormActionBtnPipe,
    DocPipe,
    ImageFormatPipe,
    DocResourcesPipe,
    BreadcrumbsComponent,
    ActiveDirectoryTypePipe,
    HiddenDottedMenuComponent,
    CrossComponent,
    AttachmentListComponent,
    AddButtonComponent,
    SpinnerButtonComponent,
    DraggableAreaComponent,
    NoFocusStealingDirective,
    OutsideClickDirective,
    AutIdDirective,
    ScrollToFirstInvalidDirective,
    FormBadgeClassPipe,
    ImageSelectComponent,
    A360ImageMultiSelectComponent,
    Ng6AgencyCustomSelectComponent,
    FilterByPipe,
    Ng6AgencyMultiSelectComponent,
    ActiveIndicatorComponent,
    PageHeaderComponent,
    Ng6AgencyTabsetComponent,
    Ng6AgencyTabComponent,
    NumeralPipe,
    IncidentsSupportPipe,
    Ng6AgencyRadioOptionComponent,
    Ng6AgencyRadioGroupComponent,
    InstanceActionsBarComponent,
    InstanceNumberPipe,
    OutlineButtonComponent,
    IncidentNatureComponent,
    IncidentNaturePipe,
    FormInstanceLinkComponent,
    SimpleBarChartComponent,
    MoveToMainDirective,
    RejectPeSummariesModalComponent,
    FtReportSubjectPipe,
    ReportStatusPipe,
    NumberWithoutCommaPipe,
    SplitButtonComponent,
    ItemCountWithHoverComponent,
    SelectUserModalRowComponent,
    SelectUserModalComponent,
    ChartComponent,
    A360SectionWithHeaderComponent,
    GoalStatusPipe,
    ByGoalStatusPipe,
    SwitchBallComponent,
    A360CardComponent,
    A360AvatarComponent,
    DocumentsListComponent,
    DocumentItemComponent,
    DocumentIconComponent,
    DueDatePipe,
    DueDateFilterPipe,
    EmptyStateComponent,
    ButtonWithDropdownComponent,
    DocumentIconComponent,
    ProfileGoalsComponent,
    UserGoalComponent,
    GoalModificationModalComponent,
    InterestsReadComponent,
    SubjectNarrativeComponent,
    CurrentStepComponent,
    InstanceItemComponent,
    InstancesListComponent,
    HeaderUserInfoComponent,
    ReadMoreComponent,
    ViewDetailsComponent,
    HighFiveViewModalComponent,
    AlertTypePipe,
    SettingItemTemplateComponent,
    BorderedButtonComponent,
    MtAssignmentModalComponent,
    AlertBadgeComponent,
    AuditLogComponentPipe,
    AuditLogEventPipe,
    LazySlicePipe,
    ActiveBadgeComponent,
    PerformanceCategoriesAverageComponent,
    PrintButtonComponent,
    ProgramExitTypeNaturePipe,
    ProgramExitTypeNatureComponent,
    ScrollOnClickDirective,
    SupportingDocumentsComponent,
    PolicyDocumentReviewListComponent,
    SnapshotCardComponent,
    NumberWithComparatorComponent,
    StorageUsageComponent,
    UsersRowExpandableComponent,
    TooltipComponent,
    TrainingCourseTypePipe,
    TrainingCourseGradingPipe,
    TrainingCourseClassStatusFilterPipe,
    A360AvatarsComponent,
    DocumentsReadOnlyListComponent,
    TrainingCourseClassStatusPipe,
    TrainingCourseRequestStatusPipe,
    QuizStatusPipe,
    StartQuizModalComponent,
    TrainingCourseProviderPipe,
    ProfileTrainingCourseClassesComponent,
    ProfileTrainingCourseClassComponent,
    GreyoutWrapComponent,
    VersionComponent,
    LabelComponent,
    IconComponent,
    IconDirective,
    TableScrollCheckDirective,
    InputComponent,
    ButtonComponent,
    TableRowComponent,
    TableDataComponent,
    MaintenanceModeComponent,
    StripHtmlPipe,
    MigrateQuillPipe,
    LogoComponent,
    TimePickerComponent,
    IconBadgeComponent,
    UserInfoCardComponent,
    AlertListFlyoutComponent,
    ProductCodeToNamePipe,
    ToggleArrowBtnComponent,
    PageNotAvailableComponent,
    RecallScoresCardComponent,
    PatternLibraryModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedModule {
  constructor(modalService: BsModalService) {
    modalService.onShown.subscribe(this.checkInClass.bind(this));
  }

  private checkInClass(): void {
    const modal = document.querySelector('modal-container[role="dialog"]:not(.fto-legacy-modal)');

    if (modal) {
      modal.classList.add('in');
      modal.classList.add('fto-legacy-modal');
    }

    // check if night-mode class is present
    const nightMode = document.querySelector('main.night-mode');
    if (nightMode) {
      // add night-mode class to modal
      modal.classList.add('night-mode');
    }
  }
}
